import React, { useEffect, useState } from 'react';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  getUnitPricingByBlockId,
  updateUnitStatus,
  getUnitStatus,
  bulkBookUnits,
  getAllActiveSalesUsersByProjectId
} from '../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import Table from '../../shared/components/layout/Table';
import { useForm } from 'react-hook-form';
import { transformData } from '../../super-admin/utils/transformData';
import toast from 'react-hot-toast';
import { UpdateStatus } from '../components/forms/UpdateStatus';
import Modal from '../../shared/components/ui/Modal';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Define all possible transitions in a single map:
 * currentStatus => array of allowed next statuses
 */
const statusTransitions = {
  Vacant: ['Blocked', 'Blocked_for_Broker', 'Booked'],

  // If current status is "Blocked", it can transition to "Vacant" or "Blocked_for_Broker".
  Blocked: ['Vacant', 'Blocked_for_Broker'],

  // If current status is "Blocked_for_Broker", it can go to "Vacant" or "Blocked".
  Blocked_for_Broker: ['Vacant', 'Blocked'],

  // If current status is "Booked", it can transition to the following four statuses.
  Booked: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues'
  ],

  // If current status is "Temporary_Booked", it can move to these four.
  Temporary_Booked: [
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues',
    'Booked'
  ],

  // If current status is "Sales_Deed_Completed", it can move to these four.
  Sales_Deed_Completed: [
    'Temporary_Booked',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues',
    'Booked'
  ],

  // If current status is "Sales_Deed_with_Pending_Payments", it can move to these four.
  Sales_Deed_with_Pending_Payments: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'No_Dues',
    'Booked'
  ],

  // If current status is "No_Dues", it can move to these four.
  No_Dues: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'Booked'
  ]
};

const UnitPricingList = ({ projectName }) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const { register } = useForm();
  const [newBlockId, setNewBlockId] = useState('');
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [updateStatusOptions, setUpdateStatusOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const { collapsed } = useSidebar();
  // Pagination removed
  const page = 1;
  const limit = 20000; // fetch more per page
  const [expandedRows, setExpandedRows] = useState([]);

  // Checkbox selection state
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState('');
  const [selectedBookAs, setSelectedBookAs] = useState('');
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);

  // Dropdown state for header portal dropdown
  const [openDropdown, setOpenDropdown] = useState(null);
  const blockHeaderRef = React.useRef(null);
  const [blockDropdownRect, setBlockDropdownRect] = useState(null);
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Select Status');

  // === Queries ===
  const { data: blocksDyna } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const {
    data: unitPricingData,
    refetch: refetchUnitPricingData,
    isFetching
  } = useQuery({
    queryKey: ['unitPricing', newBlockId, page, selectedStatus],
    queryFn: () => getUnitPricingByBlockId(newBlockId, page, limit, selectedStatus),
    keepPreviousData: true,
    enabled: !!newBlockId // Only fetch if we have a valid block ID
  });

  const { data: unitStatus } = useQuery({
    queryKey: ['unitStatus'],
    queryFn: () => getUnitStatus(),
    enabled: true
  });

  const { data: salesUsers } = useQuery({
    queryKey: ['salesUsers', projectId],
    queryFn: () => getAllActiveSalesUsersByProjectId(projectId),
    enabled: !!projectId
  });

  const handleUnitStatusUpdate = (data) => {
    const payload = { status: data?.status };
    handleUnitUpdate(payload);
  };

  // === Mutations ===
  const { mutate: handleUnitUpdate } = useMutation({
    mutationFn: (payload) => updateUnitStatus(selectedUnitId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Unit status updated successfully');
      setOpenUpdateStatus(false);
      await refetchUnitPricingData();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating unit status', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating unit status');
    }
  });

  // Bulk booking mutation
  const { mutate: handleBulkBooking } = useMutation({
    mutationFn: (payload) => bulkBookUnits(payload),
    onMutate: () => {
      setIsBookingSubmitting(true);
    },
    onSuccess: async () => {
      setIsBookingSubmitting(false);
      toast.success('Units booked successfully');
      setShowBookingModal(false);
      setSelectedUnits([]);
      setSelectedSalesPerson('');
      setSelectedBookAs('');
      await refetchUnitPricingData();
    },
    onError: (err) => {
      setIsBookingSubmitting(false);
      console.error('Error booking units', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error booking units');
    }
  });

  // === Effects ===

  // Track page view
  useEffect(() => {
    mixpanel.track('Unit Pricing List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    // Filter out only blocks that have levels, then set the first one as default
    if (Array.isArray(blocksDyna) && blocksDyna.length > 0) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      if (sorted.length > 0) {
        setBlocksDynaWithLevels(sorted);
        // If there's already a block selected, don't overwrite it;
        // otherwise default to the newest block
        setNewBlockId((prev) => (prev ? prev : sorted[0].id));
      }
    }
  }, [blocksDyna]);

  // Whenever block changes, refetch units
  useEffect(() => {
    if (newBlockId) {
      refetchUnitPricingData();
    }
  }, [newBlockId, refetchUnitPricingData]);

  // For debugging: log the statuses we fetched
  useEffect(() => {
    if (unitStatus) {
      console.log('Fetched unitStatus from API:', unitStatus);
    }
  }, [unitStatus]);

  // === Data transformations ===
  const UnitPricingFormat = [
    'id',
    'unitName',
    'cost',
    'plc_price',
    'gst',
    'extra_charges',
    'total_price',
    'accommodationName',
    'status',
    'actions'
  ];

  const RequiredUnitPricingData =
    unitPricingData?.data && unitPricingData?.data.length > 0
      ? unitPricingData.data
          .map((item) =>
            transformData(
              {
                ...item,
                cost: parseInt(item?.cost) || 0,
                plc_price: parseInt(item?.plc_price) || 0,
                gst: `₹${(parseInt(item?.gst) || 0).toLocaleString('en-IN')}.00`,
                extra_charges: `₹${(parseInt(item.extra_charges) || 0).toLocaleString('en-IN')}.00`,
                total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (parseInt(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0)
                ).toLocaleString('en-IN')}.00`,
                accommodationName: item?.accommodation?.name,
                status: item?.status
              },
              UnitPricingFormat
            )
          )
          // Sort by id in ascending order
          .sort((a, b) => a.id - b.id)
      : [];

  // === Handlers ===
  const handleSelectBlockChange = (event) => {
    const selectedBlockId = event.target.value;
    setNewBlockId(selectedBlockId);
  };

  /**
   * Dynamically filter possible status updates based on current unit status,
   * using the `statusTransitions` map above.
   */
  const handleStatusUpdate = (unitId) => {
    setOpenUpdateStatus(true);
    setSelectedUnitId(unitId);

    // Find the unit in the data
    const currentUnit = unitPricingData?.data?.find((u) => parseInt(u.id) === parseInt(unitId));
    setSelectedUnit(currentUnit);

    if (!currentUnit || !unitStatus) {
      setUpdateStatusOptions([]);
      return;
    }

    const currentStatus = currentUnit.status;
    const allowedNextStatuses = statusTransitions[currentStatus] || [];
    const newOptions = unitStatus.filter((s) => allowedNextStatuses.includes(s));
    setUpdateStatusOptions(newOptions);
  };

  // Toggle expand/collapse
  const handleToggleExpand = (unitId) => {
    setExpandedRows((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  // Pagination
  // Pagination removed
  const totalPages = 1;
  const handlePageChange = () => {};

  // Current block name for display in table
  const currentBlockName =
    blocksDynaWithLevels?.find((b) => String(b.id) === String(newBlockId))?.name || '---';

  // PortalDropdown component
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type) => {
    // toggle dropdown
    const next = openDropdown === type ? null : type;
    setOpenDropdown(next);
    if (next === 'block' && blockHeaderRef?.current) {
      setBlockDropdownRect(blockHeaderRef.current.getBoundingClientRect());
    }
    if (next === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef.current.getBoundingClientRect());
    }
  };

  const handleCloseDropdown = () => {
    setOpenDropdown(null);
    setBlockDropdownRect(null);
    setStatusDropdownRect(null);
  };

  const handleSelectDropdownItemFromHeader = (blockId) => {
    setNewBlockId(blockId);
    handleCloseDropdown();
  };

  const handleSelectStatusFromHeader = (status) => {
    setSelectedStatus(status);
    handleCloseDropdown();
  };

  // Handle unit checkbox selection
  const handleUnitSelection = (unitId, isChecked) => {
    if (isChecked) {
      setSelectedUnits((prev) => [...prev, unitId]);
    } else {
      setSelectedUnits((prev) => prev.filter((id) => id !== unitId));
    }
  };

  // Handle book button click
  const handleBookClick = () => {
    if (selectedUnits.length >= 2) {
      setShowBookingModal(true);
    }
  };

  // Handle booking submission
  const handleBookingSubmit = () => {
    if (!selectedBookAs) {
      toast.error('Please select "Book as" option');
      return;
    }

    if (!selectedSalesPerson) {
      toast.error('Please select a sales person');
      return;
    }

    // Determine status based on "Book as" selection
    const status =
      selectedBookAs === 'Close Deal' ? 'Booked_Incomplete' : 'Temporary_Booked_Incomplete';

    const payload = {
      status: status,
      ids: selectedUnits,
      sales_id: Number(selectedSalesPerson)
    };

    handleBulkBooking(payload);
  };

  return (
    <>
      {openUpdateStatus && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Update Status"
          onClose={() => setOpenUpdateStatus(false)}>
          <UpdateStatus
            onClose={() => setOpenUpdateStatus(false)}
            statuses={updateStatusOptions}
            isSubmitting={isSubmitting}
            onSubmit={handleUnitStatusUpdate}
            type={'unitPricing'}
            unitData={selectedUnit}
            projectName={projectName}
            projectId={projectId}
          />
        </Modal>
      )}

      {showBookingModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Book Units"
          onClose={() => {
            setShowBookingModal(false);
            setSelectedBookAs('');
            setSelectedSalesPerson('');
          }}>
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Please select booking type and sales person to assign these units to.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Book as <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedBookAs}
                onChange={(e) => setSelectedBookAs(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select booking type</option>
                <option value="Under Consideration">Under Consideration</option>
                <option value="Close Deal">Close Deal</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Sales Person <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedSalesPerson}
                onChange={(e) => setSelectedSalesPerson(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select a sales person</option>
                {salesUsers?.data?.map((sales) => (
                  <option key={sales.id} value={sales.id}>
                    {sales.name || sales.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedBookAs('');
                  setSelectedSalesPerson('');
                }}
                disabled={isBookingSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleBookingSubmit}
                disabled={isBookingSubmitting || !selectedBookAs || !selectedSalesPerson}>
                {isBookingSubmitting ? 'Booking...' : 'Book Units'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div
        className={`transition-all duration-300 mt-3 ease-in-out w-full flex flex-col pl-5 pr-5 bg-gray-100 overflow-hidden`}>
        {/* Book button */}
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleBookClick}
            disabled={selectedUnits.length < 2}
            customStyle={{
              padding: '0.75rem 1.5rem',
              fontSize: '14px',
              opacity: selectedUnits.length < 2 ? 0.5 : 1,
              cursor: selectedUnits.length < 2 ? 'not-allowed' : 'pointer'
            }}>
            Book ({selectedUnits.length} selected)
          </Button>
        </div>

        {/* Removed top Select Block dropdown; using header portal dropdown instead */}
        <div className="table-container mt-5 h-[calc(100vh-13rem)]  overflow-auto">
          <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
          <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg ">
            <table className=" text-left w-full ">
              <thead className=" bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                <tr className="font-normal h-[3rem] w-full">
                  <th className="px-4 py-2 border-b font-normal">Select</th>
                  <th className="px-4 py-2 border-b font-normal relative" ref={blockHeaderRef}>
                    <span>Block Name</span>
                    <button
                      className="ml-1 align-middle"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('block');
                      }}>
                      <ChevronDown size={16} />
                    </button>
                    {openDropdown === 'block' && blockDropdownRect && (
                      <PortalDropdown anchorRect={blockDropdownRect} onClose={handleCloseDropdown}>
                        {blocksDynaWithLevels?.map((block) => (
                          <div
                            key={block.id}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              String(block.id) === String(newBlockId) ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleSelectDropdownItemFromHeader(block.id)}>
                            {block.name}
                          </div>
                        ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-4 py-2 border-b font-normal ">Unit Name</th>
                  <th className="px-4 py-2 border-b font-normal">Unit Price</th>
                  <th className="px-4 py-2 border-b font-normal">Total Plc Price</th>
                  <th className="px-4 py-2 border-b font-normal">GST</th>
                  <th className="px-4 py-2 border-b font-normal">Total Price</th>
                  <th className="px-4 py-2 border-b font-normal">Extra Charges</th>
                  <th className="px-4 py-2 border-b font-normal">Discount</th>
                  <th className="px-4 py-2 border-b font-normal">Due Price</th>
                  <th className="px-4 py-2 border-b font-normal">Acc Name</th>
                  <th className="px-4 py-2 border-b font-normal relative" ref={statusHeaderRef}>
                    <span>Status</span>
                    <button
                      className="ml-1 align-middle"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('status');
                      }}>
                      <ChevronDown size={16} />
                    </button>
                    {openDropdown === 'status' && statusDropdownRect && (
                      <PortalDropdown anchorRect={statusDropdownRect} onClose={handleCloseDropdown}>
                        <div
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedStatus === 'Select Status' ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectStatusFromHeader('Select Status')}>
                          Select Status
                        </div>
                        {unitStatus?.map((s) => (
                          <div
                            key={s}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              selectedStatus === s ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleSelectStatusFromHeader(s)}>
                            {s}
                          </div>
                        ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-4 py-2 border-b font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {RequiredUnitPricingData && RequiredUnitPricingData.length > 0 ? (
                  RequiredUnitPricingData.map((row, idx) => {
                    // Find the original data for this row (to get underConsideration/closedDeal/pricingHistory)
                    const original = unitPricingData?.data?.find((u) => u.id === row.id);
                    const hasExpandable =
                      original?.underConsideration?.length > 0 ||
                      original?.closedDeal?.length > 0 ||
                      original?.pricingHistory?.length > 1;
                    return (
                      <React.Fragment key={row.id || idx}>
                        <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                          <td className="px-4 py-2">
                            {row.status === 'Vacant' && (
                              <input
                                type="checkbox"
                                checked={selectedUnits.includes(row.id)}
                                onChange={(e) => handleUnitSelection(row.id, e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded "
                              />
                            )}
                          </td>
                          <td className="px-4 py-2">{idx === 0 ? currentBlockName : ''}</td>
                          <td className="px-4 py-2">
                            {hasExpandable && (
                              <button
                                onClick={() => handleToggleExpand(row.id)}
                                style={{ marginRight: 8 }}>
                                {expandedRows.includes(row.id) ? '-' : '+'}
                              </button>
                            )}
                            {row.unitName || '---'}
                          </td>
                          <td className="px-4 py-2">{row.cost || '---'}</td>
                          <td className="px-4 py-2">{row.plc_price || '---'}</td>
                          <td className="px-4 py-2">{row.gst || '---'}</td>
                          <td className="px-4 py-2">{row.total_price || '---'}</td>
                          <td className="px-4 py-2">{row.extra_charges || '---'}</td>
                          <td className="px-4 py-2">---</td>
                          <td className="px-4 py-2">---</td>
                          <td className="px-4 py-2">{row.accommodationName || '---'}</td>
                          <td className="px-4 py-2">{row.status || '---'}</td>
                          <td className="px-4 py-2">
                            <Button
                              type="button"
                              customStyle={{
                                padding: '0.5rem 1rem',
                                fontSize: '13px',
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center'
                              }}
                              displayType={'downloadButton'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusUpdate(row.id);
                              }}>
                              Status
                            </Button>
                          </td>
                        </tr>
                        {/* Pricing History Sub-rows */}
                        {expandedRows.includes(row.id) &&
                          original?.pricingHistory?.length > 1 &&
                          original.pricingHistory.slice(1)?.map((ph, i) => {
                            // Calculate total price from cost + plc_price + gst
                            const cost = Number(ph.cost) || 0;
                            const plcPrice = Number(ph.plc_price) || 0;
                            const gst = Number(ph.gst) || 0;
                            const totalPrice = cost + plcPrice + gst;

                            return (
                              <tr key={ph.id || `ph-${i}`} className="bg-amber-50 mb-2">
                                <td className="px-4 py-2 font-semibold text-amber-800">
                                  Old Price v{i + 1}
                                </td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">
                                  {cost > 0 ? `₹${cost.toLocaleString('en-IN')}` : '---'}
                                </td>
                                <td className="px-4 py-2">
                                  {plcPrice > 0 ? `₹${plcPrice.toLocaleString('en-IN')}` : '---'}
                                </td>
                                <td className="px-4 py-2">
                                  {gst > 0 ? `₹${gst.toLocaleString('en-IN')}` : '---'}
                                </td>
                                <td className="px-4 py-2">
                                  {totalPrice > 0
                                    ? `₹${totalPrice.toLocaleString('en-IN')}`
                                    : '---'}
                                </td>
                                <td className="px-4 py-2">
                                  {ph.extra_charges
                                    ? `₹${Number(ph.extra_charges).toLocaleString('en-IN')}`
                                    : '---'}
                                </td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                              </tr>
                            );
                          })}
                        {/* Under Consideration Sub-row */}
                        {expandedRows.includes(row.id) &&
                          original?.underConsideration?.length > 0 &&
                          original.underConsideration.map((uc, i) => (
                            <tr key={uc.id || i} className="bg-blue-50 mb-2">
                              <td className="px-4 py-2 font-semibold text-blue-800">
                                Under Consideration
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {uc.unit_price || uc.customer_proposal
                                  ? `₹${Number(uc.unit_price || uc.customer_proposal).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.plc_price
                                  ? `₹${Number(uc.plc_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.gst_price || uc.gst
                                  ? `₹${Number(uc.gst_price || uc.gst).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.total_price
                                  ? `₹${Number(uc.total_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.extra_charges
                                  ? `₹${Number(uc.extra_charges).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.discount
                                  ? `₹${Number(uc.discount).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {uc.approved ? 'Approved' : 'Approval' || '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                            </tr>
                          ))}
                        {/* Deal Closed Sub-row */}
                        {expandedRows.includes(row.id) &&
                          original?.closedDeal?.length > 0 &&
                          original.closedDeal?.map((cd, i) => (
                            <tr key={cd.id || i} className="bg-green-50 mb-2">
                              <td className="px-4 py-2 font-semibold text-green-800">
                                Deal Closed
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {cd.unit_price
                                  ? `₹${Number(cd.unit_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.plc_price
                                  ? `₹${Number(cd.plc_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.gst_price
                                  ? `₹${Number(cd.gst_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.total_price
                                  ? `₹${Number(cd.total_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.extra_charges
                                  ? `₹${Number(cd.extra_charges).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.discount
                                  ? `₹${Number(cd.discount).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.due_price
                                  ? `₹${Number(cd.due_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">{cd.status || 'N/A'}</td>
                              <td className="px-4 py-2">---</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={13} className="text-center py-10 align-middle">
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                        <p className="text-gray-600 text-sm">
                          The page you are trying to access has no data.
                          <br />
                          Proceed with adding data.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnitPricingList;
